import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Facebook, Instagram, Linkedin, Mail, Phone, Twitter } from "lucide-react";

interface PreviewPanelProps {
  formData: {
    orgName: string;
    missionStatement: string;
    companyDescription: string;
    linkedIn: string;
    facebook: string;
    twitter: string;
    instagram: string;
    logoPreview: string | null;
    email: string;
    phone: string;
  };
}

export function PreviewPanel({ formData }: PreviewPanelProps) {
  return (
    <div className="h-full w-full overflow-y-auto">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center gap-6">
            {formData.logoPreview ? (
              <img
                src={formData.logoPreview}
                alt="Organization Logo"
                className="h-20 w-20 rounded-lg object-contain bg-white"
              />
            ) : (
              <div className="h-20 w-20 rounded-lg bg-gray-100 flex items-center justify-center">
                <span className="text-gray-400">Logo</span>
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {formData.orgName || "Your Organization Name"}
              </h1>
              {formData.missionStatement && (
                <p className="mt-2 text-gray-600 text-lg">{formData.missionStatement}</p>
              )}
              <div className="flex gap-4 mt-4">
                {formData.linkedIn && (
                  <a href={formData.linkedIn} target="_blank" rel="noopener noreferrer">
                    <Linkedin className="h-5 w-5 text-gray-600 hover:text-primary" />
                  </a>
                )}
                {formData.facebook && (
                  <a href={formData.facebook} target="_blank" rel="noopener noreferrer">
                    <Facebook className="h-5 w-5 text-gray-600 hover:text-primary" />
                  </a>
                )}
                {formData.twitter && (
                  <a href={formData.twitter} target="_blank" rel="noopener noreferrer">
                    <Twitter className="h-5 w-5 text-gray-600 hover:text-primary" />
                  </a>
                )}
                {formData.instagram && (
                  <a href={formData.instagram} target="_blank" rel="noopener noreferrer">
                    <Instagram className="h-5 w-5 text-gray-600 hover:text-primary" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-4">Volunteering Opportunities</h2>
          <div className="space-y-4">
            {[1, 2, 3].map((index) => (
              <div
                key={index}
                className={cn(
                  "p-4 border rounded-lg",
                  "animate-pulse bg-gray-50"
                )}
              >
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>

        {formData.companyDescription && (
          <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-4">About Us</h2>
            <p className="text-gray-600">{formData.companyDescription}</p>
          </div>
        )}

        {/* Social Links */}
        {(formData.linkedIn || formData.facebook || formData.twitter || formData.instagram) && (
          <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-4">Connect With Us</h2>
            <div className="flex flex-wrap gap-4">
              {formData.linkedIn && (
                <Button variant="outline" size="sm">LinkedIn</Button>
              )}
              {formData.facebook && (
                <Button variant="outline" size="sm">Facebook</Button>
              )}
              {formData.twitter && (
                <Button variant="outline" size="sm">Twitter</Button>
              )}
              {formData.instagram && (
                <Button variant="outline" size="sm">Instagram</Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-100 mt-auto border-t">
        <div className="container mx-auto px-6 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
              {formData.email && (
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4 text-gray-600" />
                  <a href={`mailto:${formData.email}`} className="text-gray-600 hover:text-primary">
                    {formData.email}
                  </a>
                </div>
              )}
              {formData.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-600" />
                  <a href={`tel:${formData.phone}`} className="text-gray-600 hover:text-primary">
                    {formData.phone}
                  </a>
                </div>
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Follow Us</h3>
              <div className="flex gap-4">
                {formData.linkedIn && (
                  <a href={formData.linkedIn} target="_blank" rel="noopener noreferrer">
                    <Linkedin className="h-5 w-5 text-gray-600 hover:text-primary" />
                  </a>
                )}
                {formData.facebook && (
                  <a href={formData.facebook} target="_blank" rel="noopener noreferrer">
                    <Facebook className="h-5 w-5 text-gray-600 hover:text-primary" />
                  </a>
                )}
                {formData.twitter && (
                  <a href={formData.twitter} target="_blank" rel="noopener noreferrer">
                    <Twitter className="h-5 w-5 text-gray-600 hover:text-primary" />
                  </a>
                )}
                {formData.instagram && (
                  <a href={formData.instagram} target="_blank" rel="noopener noreferrer">
                    <Instagram className="h-5 w-5 text-gray-600 hover:text-primary" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
